import { Injectable } from '@angular/core';
import { LocaleService } from './locale.service';
import { DateRange } from '../../../models/date-range';
import { Clock } from '../../../utils/clock';
import parse from 'date-fns/parse';
import { fr, frCA, enUS, enCA } from 'date-fns/locale';
import { format, isValid, sub } from 'date-fns';

@Injectable({
    providedIn: 'root'
})
export class DateService {

    private readonly REGEX_TO_RANGE = /^(?:from )?(.*) to (.*)/;
    private readonly REGEX_ISO_DATE = /^(20\d\d)-([0-1]?\d)-([0-3]?\d)$/;
    private readonly REGEX_DATE = /^([0-3]?\d)[ \-\/]([0-1]?\d)[ \-\/](20\d\d)$/;
    private readonly REGEX_MONTH_DAY = /^([A-Za-z]{3,9})[ \-\/](\d?\d)$/;
    private readonly REGEX_DAY_MONTH = /^(\d?\d)[ \-\/]([A-Za-z]{3,9})$/;
    private readonly REGEX_MONTH = /^[A-Za-z]{3,9}$/;
    private readonly REGEX_PERIODS = [
        { r: /^[a-z ]*(?:last |latest |-|)(\d+) ?d.*$/, p: 'P$1D' },
        { r: /^[a-z ]*(?:last |latest |-|)(\d+) ?m.*$/, p: 'P$1M' },
        { r: /^[a-z ]*(?:last |latest |-|)(\d+) ?w.*$/, p: 'P$1W' },
        { r: /^[a-z ]*(?:last |latest |-|)(\d+) ?y.*$/, p: 'P$1Y' },
        { r: /^[a-z ]*(?:last |latest |-|)mont.*$/, p: 'P1M' }
    ];
    private readonly DAYS = {
        en: {
            0: 'Sunday',
            1: 'Monday',
            2: 'Tuesday',
            3: 'Wednesday',
            4: 'Thursday',
            5: 'Friday',
            6: 'Saturday'
        }
    };
    private readonly MONTHS = {
        en: {
            0: 'January',
            1: 'February',
            2: 'March',
            3: 'April',
            4: 'May',
            5: 'June',
            6: 'July',
            7: 'August',
            8: 'September',
            9: 'October',
            10: 'November',
            11: 'December'
        }
    };

    private readonly FORMAT_YEAR_MONTH_DAY = 'y/M/d';
    private readonly FORMAT_MONTH_DAY_YEAR = 'M/d/y';
    private readonly FORMAT_DAY_MONTH_YEAR = 'd/M/y';
    private readonly FORMAT_YEAR_MONTH_DAY_2 = 'y-M-d';
    private readonly FORMAT_DAY_MONTH_YEAR_2 = 'd-M-y';
    private readonly FORMAT_DAY_MONTH = 'd-MMM';
    private readonly FORMAT_MONTH_DAY = 'MMM-d';

    private readonly PARSERS = {
        'y/M/d': {},
        'd/M/y': {},
        'M/d/y': {},
        'y-M-d': {},
        'd-M-y': {},
        'd-MMM': { months: 3 },
        'MMM-d': { months: 3 },
        'eeee': {},
        'MMMM': { months: 3 }
    };

    private readonly locale: Locale;

    public static isoDate(date: Date): string {
        return format(date, 'yyyy-MM-dd');
    }

    constructor(private localeService: LocaleService) {
        switch (this.localeService.getUserLocale()) {
            case 'fr-CA':
                this.locale = frCA;
                break;
            case 'fa-FR':
                this.locale = fr;
                break;
            case 'en-CA':
                this.locale = enCA;
                break;
            default:
                this.locale = enUS;
                break;
        }
    }

    private dayTable(): string[] {
        return this.DAYS[this.localeService.getSupportedUserLocale()];
    }

    private monthTable(): string[] {
        return this.MONTHS[this.localeService.getSupportedUserLocale()];
    }

    private matchFromTable(s: string, table): number {
        if (s.length >= 3) {
            for (const n of Object.keys(table)) {
                if (table[n].toLowerCase().substring(0, s.length) === s.toLowerCase().substring(0, s.length)) {
                    return parseInt(n, 10);
                }
            }
        }
        return null;
    }

    private dayIndexFromName(s: string): number {
        return this.matchFromTable(s, this.dayTable());
    }

    private monthIndexFromName(s: string): number {
        return this.matchFromTable(s, this.monthTable());
    }

    private matchDate(s: string, d: Date): boolean {
        const day = this.dayIndexFromName(s);
        if (day !== null) {
            return d.getDay() === day;
        }
        const month = this.monthIndexFromName(s);
        if (month !== null) {
            return d.getMonth() === month;
        }
        return false;
    }

    private decomposeDate(s: string, r: RegExp, dayIndex: number, monthIndex: number, yearIndex: number): Date {
        const d = s.match(r);
        if (d) {
            const year = parseInt(d[yearIndex], 10);
            const month = parseInt(d[monthIndex], 10);
            const day = parseInt(d[dayIndex], 10);
            if (year > 2000 && year < 2099 && month > 0 && month < 13 && day > 0 && day < 31) {
                return new Date(year, month - 1, day);
            }
        }
        return null;
    }

    private dateFromUserInput(s: string, start: Date, forward: boolean = false): Date {
        for (const parser of Object.keys(this.PARSERS)) {
            const offset = this.PARSERS[parser];
            const d = parse(s, parser, sub(start, offset), { locale: this.locale });
            if (isValid(d)) {
                return d;
            }
        }
        return null;

        const matchers = [
            { r: this.REGEX_ISO_DATE, y: 1, m: 2, d: 3 },
            { r: this.REGEX_DATE, y: 3, m: 2, d: 1 }
        ];
        for (const matcher of matchers) {
            const d = this.decomposeDate(s, matcher.r, matcher.d, matcher.m, matcher.y);
            if (d) {
                return d;
            }
        }
        const m1 = s.match(this.REGEX_DAY_MONTH);
        if (m1) {
            const monthIndex = this.monthIndexFromName(m1[2]);
            const dayIndex = parseInt(m1[1], 10);
            if (monthIndex !== null && dayIndex > 0 && dayIndex < 32) {
                return new Date(start.getFullYear(), monthIndex, dayIndex);
            }
        }
        const m2 = s.match(this.REGEX_MONTH_DAY);
        if (m2) {
            const monthIndex = this.monthIndexFromName(m2[1]);
            const dayIndex = parseInt(m2[2], 10);
            if (monthIndex !== null && dayIndex > 0 && dayIndex < 32) {
                return new Date(start.getFullYear(), monthIndex, dayIndex);
            }
        }
        let lastMatch = null;
        let current = start;
        const dayNumber = this.dayIndexFromName(s);
        if (dayNumber !== null) {
            while (true) {
                if (current.getDay() === dayNumber) {
                    lastMatch = current;
                } else if (lastMatch) {
                    return lastMatch;
                }
                current = Clock.advanceDays(current, forward ? +1 : -1);
            }
        }
        const monthNumber = this.monthIndexFromName(s);
        if (monthNumber !== null) {
            let dt = new Date(start.getFullYear(), monthNumber, 1);
            if (dt > start && !forward) {
                dt.setFullYear(start.getFullYear() - 1);
            }
            if (forward) {
                dt = Clock.lastDayOfMonth(dt);
            }
            return dt;
        }
        return null;
    }

    public interpretUserDateInput(s: string): DateRange {
        s = s.toLocaleLowerCase();
        if (s.match(/^20\d\d$/)) {
            return {
                valid: true,
                rangeStart: s + '-01-01',
                rangeEnd: s + '-12-31',
                period: null
            };
        }
        for (const x of this.REGEX_PERIODS) {
            if (s.match(x.r)) {
                return {
                    valid: true,
                    rangeStart: null,
                    rangeEnd: null,
                    period: s.replace(x.r, x.p)
                };
            }
        }
        const matchTo = s.match(this.REGEX_TO_RANGE);
        if (matchTo) {
            const dt1 = this.dateFromUserInput(matchTo[1], Clock.currentDate(), false);
            if (dt1) {
                const dt2 = this.dateFromUserInput(matchTo[2], dt1, true);
                if (dt2) {
                    return {
                        valid: true,
                        rangeStart: Clock.isoDate(dt1),
                        rangeEnd: Clock.isoDate(dt2),
                        period: null
                    };
                }
            }
        } else {
            const dt1 = this.dateFromUserInput(s, Clock.currentDate(), false);
            if (dt1) {
                let dt2 = dt1;
                if (s.match(this.REGEX_MONTH) && this.monthIndexFromName(s) !== null) {
                    dt2 = Clock.lastDayOfMonth(dt1);
                }
                return {
                    valid: true,
                    rangeStart: Clock.isoDate(dt1),
                    rangeEnd: Clock.isoDate(dt2),
                    period: null
                };
            }
        }
        return {
            valid: false,
            rangeStart: null,
            rangeEnd: null,
            period: null
        };
    }

    public renderShortDate(date: Date | string): string {
        const dt = date instanceof Date ? date : parse(date, 'yyyy-MM-dd', new Date(), { locale: this.locale });
        if (isValid(dt)) {
            return format(dt, 'MMM d, y');
        }
        return 'n/a';
        const monthName = this.monthTable()[dt.getMonth()].substring(0, 3);
        return `${monthName} ${dt.getDate()}, ${dt.getFullYear()}`;
    }

    public renderShortMonthName(date: Date | string): string {
        const dt = date instanceof Date ? date : parse(date, 'yyyy-MM-dd', new Date(), { locale: this.locale });
        if (isValid(dt)) {
            return format(dt, 'MMM');
        }
        return 'n/a';

        return this.monthTable()[dt.getMonth()].substring(0, 3);
    }


}
